import type { FC } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import useDeepCompareEffect from "use-deep-compare-effect";

import type { DisplayCurrencyDecimalsMap, MarketDataMap } from "../hooks";
import { useMarketExchangeRates } from "../hooks";
import { getPriceInUSD } from "../utils/markets/getPriceInUSD";
import type { SerumMarketPrices } from "../utils/markets/serum";
import { getSerumMarketPrices } from "../utils/markets/serum";
import { tokenInfoReady } from "../utils/tokenInfo";
import { useLocalStorageState } from "./../utils/utils";
import { useMarketDataMapContext, useMarketDataMapContextRefresher } from "./marketDataMap";
import { useXStepContextRefresher } from "./xStep";

export interface MarketsContextState {
  marketsInitialised: boolean;
  marketDataMap: MarketDataMap;
  exchangeRateForDisplayedCurrency: number;
  displayCurrency: string;
  setDisplayCurrency: (updatedDisplayCurrency: string) => void;
  fiatDisplayCurrencies: DisplayCurrencyDecimalsMap;
  cryptoDisplayCurrencies: DisplayCurrencyDecimalsMap;
  displayCurrencies: string[];
  refreshing: boolean;
  refreshPrices: () => Promise<void>;
  priceInUSD: (mint: string) => number | null;
  getSerumMarketPrices: (mint: string) => SerumMarketPrices;
}

const MarketsContext = createContext<MarketsContextState>({
  marketsInitialised: false,
  marketDataMap: new Map(),
  exchangeRateForDisplayedCurrency: 1,
  displayCurrency: "USD",
  setDisplayCurrency: () => {},
  fiatDisplayCurrencies: {},
  cryptoDisplayCurrencies: {},
  displayCurrencies: [],
  refreshing: false,
  refreshPrices: Promise.resolve,
  priceInUSD: () => null,
  getSerumMarketPrices: () => ({
    bid: null,
    mid: null,
    ask: null,
  }),
});

const getPriceInUSDCallback = (marketDataMap: MarketDataMap) => (mintAddress: string) => {
  // clone function for immutability. This triggers refresh
  // for all logic using priceInUSD
  const __fn = getPriceInUSD.bind({});
  return __fn(marketDataMap, mintAddress);
};

const getSerumMarketPricesCallback = (marketDataMap: MarketDataMap) => (mintAddress: string) =>
  getSerumMarketPrices(marketDataMap.get(mintAddress));

export const MarketProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const refreshXStep = useXStepContextRefresher();
  const marketDataMap = useMarketDataMapContext();
  const refreshMarketData = useMarketDataMapContextRefresher();
  const [tokenInfoLoaded, setTokenInfoLoaded] = useState(false);

  const {
    exchangeRates,
    fiatDisplayCurrencies,
    cryptoDisplayCurrencies,
    refresh: refreshExchangeRates,
  } = useMarketExchangeRates();

  const [displayCurrency, setDisplayCurrency] = useLocalStorageState("displayCurrency", "USD");
  const exchangeRateForDisplayedCurrency = exchangeRates[displayCurrency.toLowerCase()];
  const [priceInUSD, setPriceInUSD] = useState<MarketsContextState["priceInUSD"]>(() =>
    getPriceInUSDCallback(marketDataMap),
  );
  const [getSerumMarketPrices, setGetSerumMarketPrices] = useState<MarketsContextState["getSerumMarketPrices"]>(() =>
    getSerumMarketPricesCallback(marketDataMap),
  );
  const [refreshing, setRefreshing] = useState(false);

  useDeepCompareEffect(() => {
    setPriceInUSD(() => getPriceInUSDCallback(marketDataMap));
    setGetSerumMarketPrices(() => getSerumMarketPricesCallback(marketDataMap));
  }, [marketDataMap]);

  useEffect(() => {
    let retries = 0;
    const checkTokenInfoLoaded = () => {
      // cap retries at 20 seconds
      if (!tokenInfoReady && retries <= 2000) {
        setTimeout(checkTokenInfoLoaded, 100);
        retries += 1;
      } else {
        setTokenInfoLoaded(true);
      }
    };
    void checkTokenInfoLoaded();
  }, []);

  const memoizedValue = useMemo(
    () => ({
      marketsInitialised: marketDataMap.size > 0 && tokenInfoLoaded,
      marketDataMap,
      exchangeRateForDisplayedCurrency,
      refreshing,
      refreshPrices: async () => {
        setRefreshing(true);
        await refreshExchangeRates();
        await refreshMarketData();
        await refreshXStep();
        setRefreshing(false);
      },
      displayCurrency,
      setDisplayCurrency,
      fiatDisplayCurrencies,
      cryptoDisplayCurrencies,
      displayCurrencies: [...Object.keys(fiatDisplayCurrencies), ...Object.keys(cryptoDisplayCurrencies)],
      priceInUSD,
      getSerumMarketPrices,
    }),
    [
      cryptoDisplayCurrencies,
      displayCurrency,
      exchangeRateForDisplayedCurrency,
      fiatDisplayCurrencies,
      getSerumMarketPrices,
      marketDataMap,
      priceInUSD,
      refreshExchangeRates,
      refreshMarketData,
      refreshXStep,
      refreshing,
      setDisplayCurrency,
      tokenInfoLoaded,
    ],
  );

  return <MarketsContext.Provider value={memoizedValue}>{children}</MarketsContext.Provider>;
};

export const useMarkets = () => {
  const context = useContext(MarketsContext);
  return context;
};
